import React, { useState } from 'react';
import { Comment } from '../../types/comment';
import { useAuth } from '../../contexts/AuthContext';

interface CommentsPanelProps {
  comments: Comment[];
  selectedComment: Comment | null;
  onCommentSelect: (comment: Comment | null) => void;
  onDeleteComment: (commentId: string) => void;
  onUpdateComment: (commentId: string, content: string) => void;
}

const CommentsPanel: React.FC<CommentsPanelProps> = ({
  comments,
  selectedComment,
  onCommentSelect,
  onDeleteComment,
  onUpdateComment
}) => {
  const { user } = useAuth();
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');

  const handleEditStart = (comment: Comment) => {
    setEditingComment(comment.id);
    setEditContent(comment.content);
  };

  const handleEditSave = (commentId: string) => {
    onUpdateComment(commentId, editContent);
    setEditingComment(null);
    setEditContent('');
  };

  const handleEditCancel = () => {
    setEditingComment(null);
    setEditContent('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{
      backgroundColor: '#f8f9fa',
      border: '1px solid #dee2e6',
      borderRadius: '8px',
      padding: '1rem',
      maxHeight: '600px',
      overflowY: 'auto',
      position: 'relative'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem',
        paddingBottom: '0.5rem',
        borderBottom: '2px solid #dee2e6'
      }}>
        <h3 style={{ margin: 0, color: '#333', fontSize: '1.1rem' }}>
          Comments ({comments.length})
        </h3>
        {selectedComment && (
          <button
            onClick={() => onCommentSelect(null)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.2rem',
              cursor: 'pointer',
              color: '#666'
            }}
            title="Clear selection"
          >
            ✕
          </button>
        )}
      </div>

      {comments.length === 0 ? (
        <div style={{
          textAlign: 'center',
          color: '#666',
          padding: '2rem 1rem',
          fontStyle: 'italic'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>💬</div>
          <p>No comments yet.</p>
          <p style={{ fontSize: '0.9rem' }}>
            Click on any paragraph in the document to add your first comment.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {comments.map((comment) => (
            <div
              key={comment.id}
              style={{
                backgroundColor: selectedComment?.id === comment.id ? '#e3f2fd' : 'white',
                border: selectedComment?.id === comment.id ? '2px solid #2196f3' : '1px solid #dee2e6',
                borderRadius: '8px',
                padding: '1rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onClick={() => onCommentSelect(comment)}
            >
              {/* Block reference */}
              <div style={{
                backgroundColor: '#e3f2fd',
                padding: '0.5rem',
                borderRadius: '4px',
                marginBottom: '0.75rem',
                fontSize: '0.85rem',
                fontStyle: 'italic',
                border: '1px solid #bbdefb'
              }}>
                📍 Block: {comment.blockId.split('-').pop()}
              </div>

              {/* Comment content */}
              {editingComment === comment.id ? (
                <div>
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    style={{
                      width: '100%',
                      minHeight: '60px',
                      padding: '0.5rem',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      resize: 'vertical'
                    }}
                    autoFocus
                  />
                  <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    marginTop: '0.5rem'
                  }}>
                    <button
                      onClick={() => handleEditSave(comment.id)}
                      style={{
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '0.25rem 0.75rem',
                        fontSize: '0.8rem',
                        cursor: 'pointer'
                      }}
                    >
                      Save
                    </button>
                    <button
                      onClick={handleEditCancel}
                      style={{
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '0.25rem 0.75rem',
                        fontSize: '0.8rem',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{
                  marginBottom: '0.75rem',
                  lineHeight: '1.4',
                  color: '#333'
                }}>
                  {comment.content}
                </div>
              )}

              {/* Comment metadata and actions */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '0.75rem',
                color: '#666'
              }}>
                <div>
                  <strong>{comment.user.firstName} {comment.user.lastName}</strong>
                  <br />
                  {formatDate(comment.createdAt)}
                </div>
                
                {comment.userId === user?.id && editingComment !== comment.id && (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditStart(comment);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#0066cc',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        textDecoration: 'underline'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm('Delete this comment?')) {
                          onDeleteComment(comment.id);
                        }
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#dc3545',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        textDecoration: 'underline'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentsPanel;
